import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Bot } from "lucide-react";
import { API } from "@/lib/api";

const genId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

const GREETING =
  "Hi! I'm Jero Assistant 🤝 Tell me what IT solution you need — equipment, networking, security, cloud, support or a new website — and I'll point you the right way.";

function Thinking() {
  return (
    <span className="inline-flex items-center gap-2.5" data-testid="chatbot-thinking">
      <span className="grid grid-cols-3 gap-[3px]">
        {Array.from({ length: 9 }).map((_, i) => (
          <span
            key={i}
            className="h-[5px] w-[5px] rounded-full bg-brand-500"
            style={{ animation: "jeroPulse 1s ease-in-out infinite", animationDelay: `${(i % 3 + Math.floor(i / 3)) * 0.12}s` }}
          />
        ))}
      </span>
      <span className="text-brand-600 font-medium">Thinking…</span>
    </span>
  );
}

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(genId);
  const [messages, setMessages] = useState([{ id: genId(), role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { id: genId(), role: "user", content: text }, { id: genId(), role: "assistant", content: "" }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          ...copy[copy.length - 1],
          role: "assistant",
          content: "Sorry, something went wrong. Please email support@jero-onetechnologies.com.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="chatbot-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[60] h-14 w-14 rounded-full bg-brand text-white shadow-xl shadow-brand/30 flex items-center justify-center hover:bg-brand-700 hover:scale-105 transition-transform"
        aria-label="Chat with Jero Assistant"
      >
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </button>

      {open && (
        <div
          data-testid="chatbot-window"
          className="fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] sm:w-96 h-[70vh] sm:h-[520px] bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-brand/15 flex flex-col overflow-hidden"
        >
          <div className="bg-brand text-white px-4 py-3.5 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-white/15 flex items-center justify-center">
              <Bot className="h-5 w-5" />
            </div>
            <div>
              <p className="font-heading font-semibold leading-tight">Jero Assistant</p>
              <p className="text-xs text-white/70">IT Solutions Advisor · online</p>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto chat-scroll p-4 space-y-3 bg-brand-50/40">
            {messages.map((m, i) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-brand text-white rounded-br-sm"
                      : "bg-white border border-brand/10 text-[#0A0F0B] rounded-bl-sm"
                  }`}
                >
                  {m.content ? m.content : (busy && i === messages.length - 1 ? <Thinking /> : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-brand/10 bg-white">
            <div className="flex items-center gap-2">
              <input
                data-testid="chatbot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about any IT solution…"
                className="flex-1 rounded-full border border-brand/20 bg-white px-4 py-2.5 text-sm text-[#0A0F0B] caret-[#1B5E35] placeholder:text-[#6B7C6E] focus:outline-none focus:ring-2 focus:ring-brand-500/40"
              />
              <button
                data-testid="chatbot-send"
                onClick={send}
                disabled={busy}
                className="h-10 w-10 rounded-full bg-brand text-white flex items-center justify-center hover:bg-brand-700 disabled:opacity-50 transition-colors"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
